import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { OrderType, client, BulkOrderItem, BulkOrdersRequest, OrderFormItemUpdate, OrderFormUpdate, Market,
  MarketSupplier, BulkOrderSubmitRequest, OrderSubmitItem, DemandModifier } from '../../../../app/services/api/orderManagementClient';
import { ODSAvailability } from '../components/BulkOrderDateCalendar';
import { useSaveDemandModifier } from '../../DemandModifiers/hooks/useSaveDemandModifier';
import { DemandModifierType } from '../../DemandModifiers/types/DemandModifierTypes';
import { base64ToBlob } from '../../../../utils';
import { isOrderTypeDSD } from '../../../../enums/OrderType';
import { Role } from '../../../../enums/Role';

interface SelectedStore {
  storeNumber?: string;
  franchiseName?: string;
  franchiseUUID?: string;
}

interface UseBulkOrderDataOptions {
  selectedStore: SelectedStore | null | undefined;
  logError: (error: unknown) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
  appliedItemSearch: string;
  appliedCategory: number;
  clearFilters: () => void;
  hasPermissionTo: (permissions: Role[]) => boolean;
}

export const useBulkOrderData = ({ selectedStore, logError, t, appliedItemSearch, appliedCategory, clearFilters,
  hasPermissionTo,
}: UseBulkOrderDataOptions) => {
  const [loading, setLoading] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState<string>('');
  const [orderType, setOrderType] = useState<number>(-1);
  const [supplierNumber, setSupplierNumber] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [orderTypes, setOrderTypes] = useState<OrderType[]>([]);
  const [suppliers, setSuppliers] = useState<MarketSupplier[]>([]);
  const [categories, setCategories] = useState<{ id: number; name: string; parentName: string }[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [bulkOrderItems, setBulkOrderItems] = useState<BulkOrderItem[]>([]);
  const [selectedStores, setSelectedStores] = useState<Set<string>>(new Set());
  const [odsAvailability, setOdsAvailability] = useState<Record<string, ODSAvailability>>({});
  const [odsAvailabilityLoaded, setOdsAvailabilityLoaded] = useState(false);
  const [showQuantityWarningDialog, setShowQuantityWarningDialog] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [bulkOrdersLoadVersion, setBulkOrdersLoadVersion] = useState(0);
  const [isDemandModifierModalOpen, setIsDemandModifierModalOpen] = useState(false);
  const [editingModifier, setEditingModifier] = useState<DemandModifier | null>(null);
  const [skuError, setSkuError] = useState<string>('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({ open: false, message: '', severity: 'info' });

  const bulkOrderItemsRef = useRef<BulkOrderItem[]>([]);
  useEffect(() => { bulkOrderItemsRef.current = bulkOrderItems; }, [bulkOrderItems]);

  const isDsdOrderType = isOrderTypeDSD(orderType);

  const categoryOptions = React.useMemo(() => [
    { id: -1, name: t('allCategories'), parentName: '' },
    ...categories,
  ], [categories, t]);

  const getCategoryOptionLabel = (option: { id: number; name: string; parentName: string }) => {
    if (!option) return '';
    if (option.parentName) return `${option.parentName} > ${option.name}`;
    return option.name;
  };

  const { saveDemandModifier } = useSaveDemandModifier({ storeNumber: selectedStore?.storeNumber });

  // Snackbar helpers 
  const handleSnackbarOpen = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  useEffect(() => {
    loadMarkets();
  }, [selectedStore?.storeNumber]);

  useEffect(() => {
    if (markets.length > 0 && !selectedMarket && selectedStore?.franchiseUUID) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const userMarket = markets.find(m => (m as any).uuid === selectedStore.franchiseUUID);
      if (userMarket) {
        setSelectedMarket(userMarket.customerTypeId.toString());
      }
    }
  }, [markets, selectedStore, selectedMarket]);

  useEffect(() => {
    if (selectedMarket) {
      loadData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMarket]);

  useEffect(() => {
    if (orderTypes.length > 0 && orderType === -1) {
      setOrderType(orderTypes[0].id);
    } else if (orderTypes.length === 0 && orderType !== -1) {
      setOrderType(-1);
    }
  }, [orderTypes, orderType]);

  useEffect(() => {
    if (selectedMarket && orderType !== -1) {
      fetchODSAvailability();
      fetchCategories();
    } else {
      setOdsAvailabilityLoaded(false);
      setOdsAvailability({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMarket, orderType, supplierNumber]);

  useEffect(() => {
    if (selectedMarket && isDsdOrderType) {
      fetchSuppliersForMarket();
      return;
    }
    setSuppliers([]);
    setSupplierNumber('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMarket, orderType, markets]);

  useEffect(() => {
    if (Object.keys(odsAvailability).length > 0) {
      const availableDates = Object.entries(odsAvailability)
        .filter(([_, availability]) => availability.availableStores > 0)
        .map(([dateStr]) => dayjs(dateStr))
        .sort((a, b) => a.isAfter(b) ? 1 : -1);

      if (availableDates.length > 0) {
        setSelectedDate(availableDates[0]);
      } else {
        setSelectedDate(null);
        setBulkOrderItems([]);
      }
    } else {
      setSelectedDate(null);
      setBulkOrderItems([]);
    }
  }, [odsAvailability]);

  useEffect(() => {
    if (selectedMarket && orderType !== -1 && selectedDate) {
      if (isDsdOrderType && !supplierNumber) {
        setBulkOrderItems([]);
        return;
      }
      const dateStr = selectedDate.format('YYYY-MM-DD');
      const availability = odsAvailability[dateStr];
      if (availability && availability.availableStores > 0) {
        fetchBulkOrderData();
      } else {
        setBulkOrderItems([]);
      }
    } else {
      setBulkOrderItems([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedCategory, appliedItemSearch, selectedMarket, orderType, supplierNumber, selectedDate, odsAvailability]);

  // Data fetch functions 
  const loadMarkets = async () => {
    try {
      if (!selectedStore?.storeNumber) {
        setMarkets([]);
        return;
      }

      const companyResult = await client.getCompanyIdByStoreNumber(selectedStore.storeNumber);
      if (!companyResult.success || !companyResult.data) {
        setMarkets([]);
        return;
      }

      const response = await client.getMarkets({
        companyId: companyResult.data.toString(),
        pageNumber: 0,
        pageSize: 2147483647,
      });

      if (response.success && response.data) {
        setMarkets(response.data.items || []);
      }
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('fetchOrderError') + ' ' + error, 'error');
    }
  };

  const loadData = async () => {
    setLoading(true);
    await fetchOrderTypes();
    setLoading(false);
  };

  const fetchODSAvailability = async () => {
    if (!selectedMarket || orderType === -1) return;
    if (isDsdOrderType && !supplierNumber) {
      setOdsAvailabilityLoaded(true);
      setOdsAvailability({});
      return;
    }

    setOdsAvailabilityLoaded(false);
    try {
      const startDate = dayjs().format('DD-MM-YYYY');
      const endDate = dayjs().add(30, 'days').format('DD-MM-YYYY');

      const result = await client.getODSAvailability({
        customerTypeId: Number(selectedMarket),
        orderTypeId: orderType,
        supplierNumber: isDsdOrderType ? supplierNumber : undefined,
        startDate,
        endDate,
      });

      if (result.success && result.data) {
        if (Array.isArray(result.data)) {
          const transformed: Record<string, ODSAvailability> = {};
          result.data.forEach((item: { Key: string; Value: ODSAvailability }) => {
            if (item.Key && item.Value) {
              transformed[item.Key] = item.Value;
            }
          });
          setOdsAvailability(transformed);
        } else {
          setOdsAvailability(result.data);
        }
      } else {
        setOdsAvailability({});
      }
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('fetchODSError') + ' ' + error, 'error');
      setOdsAvailability({});
    } finally {
      setOdsAvailabilityLoaded(true);
    }
  };

  const fetchOrderTypes = async () => {
    try {
      const result = await client.getFranchiseOrderTypes(Number(selectedMarket));
      if (result.success && result.data) {
        setOrderTypes(result.data);
      }
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('fetchOrderError') + ' ' + error, 'error');
      setOrderTypes([]);
    }
  };

  const fetchCategories = async () => {
    if (!selectedMarket || orderType === -1) return;

    try {
      const result = await client.getFranchiseCategories(Number(selectedMarket), orderType);
      if (result.success && result.data) {
        setCategories(result.data);
      }
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('fetchCategoriesError') + ' ' + error, 'error');
      setCategories([]);
    }
  };

  const fetchBulkOrderData = async () => {
    if (!selectedMarket || orderType === -1 || !selectedDate) {
      setBulkOrderItems([]);
      return;
    }

    if (isDsdOrderType && !supplierNumber) {
      setBulkOrderItems([]);
      return;
    }

    const dateStr = selectedDate.format('YYYY-MM-DD');
    const availability = odsAvailability[dateStr];

    if (!availability || availability.availableStores === 0) {
      setBulkOrderItems([]);
      return;
    }

    setLoading(true);
    try {
      const request: BulkOrdersRequest = {
        customerTypeId: Number(selectedMarket),
        orderTypeId: orderType,
        deliveryDate: selectedDate.format('YYYY-MM-DD'),
        supplierNumber: isDsdOrderType ? supplierNumber : undefined,
        categoryId: appliedCategory !== -1 ? appliedCategory : undefined,
        itemSearch: appliedItemSearch || undefined,
      };

      const response = await client.getBulkOrderData(request);

      if (response.success) {
        setBulkOrderItems(response.data || []);
        setBulkOrdersLoadVersion(v => v + 1);
      } else {
        handleSnackbarOpen(response.message || t('fetchOrderError'), 'error');
        setBulkOrderItems([]);
      }
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('fetchOrderError') + ' ' + error, 'error');
      setBulkOrderItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliersForMarket = async () => {
    if (!selectedMarket) {
      setSuppliers([]);
      setSupplierNumber('');
      return;
    }

    const selectedMarketData = markets.find(m => m.customerTypeId.toString() === selectedMarket);
    if (!selectedMarketData) {
      setSuppliers([]);
      setSupplierNumber('');
      return;
    }

    try {
      const result = await client.getMarketSuppliers({
        companyId: selectedMarketData.companyId.toString(),
        customerTypeId: selectedMarket,
        sort: 'supplierName',
        direction: 'asc',
        pageNumber: 0,
        pageSize: 2147483647,
      });

      if (result.success && result.data?.items) {
        const marketSuppliers = result.data.items;
        setSuppliers(marketSuppliers);
        setSupplierNumber((current) => {
          if (current && marketSuppliers.some(s => s.supplierNumber === current)) {
            return current;
          }
          return marketSuppliers.length > 0 ? marketSuppliers[0].supplierNumber : '';
        });
      } else {
        setSuppliers([]);
        setSupplierNumber('');
      }
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('fetchOrderError') + ' ' + error, 'error');
      setSuppliers([]);
      setSupplierNumber('');
    }
  };

  const handleStoreGroupSelection = (storeNumber: string, checked: boolean) => {
    setSelectedStores((prev) => {
      const newSet = new Set(prev);
      if (checked) newSet.add(storeNumber);
      else newSet.delete(storeNumber);
      return newSet;
    });
  };

  // Dropdown/filter change handlers
  const handleMarketChange = (newMarket: string) => {
    setBulkOrderItems([]);
    setSelectedStores(new Set());
    setCategories([]);
    setOrderTypes([]);
    setOrderType(-1);
    setSuppliers([]);
    setSupplierNumber('');
    setSelectedDate(null);
    setOdsAvailability({});
    clearFilters();
    setSelectedMarket(newMarket);
  };

  const handleOrderTypeChange = (newOrderType: number) => {
    setBulkOrderItems([]);
    setSelectedStores(new Set());
    setSelectedDate(null);
    setOdsAvailability({});
    clearFilters();
    setSuppliers([]);
    setSupplierNumber('');
    setOrderType(newOrderType);
  };

  const handleSupplierChange = (newSupplierNumber: string) => {
    setSupplierNumber(newSupplierNumber);
    setSelectedDate(null);
    setBulkOrderItems([]);
    setSelectedStores(new Set());
    setOdsAvailability({});
    setOdsAvailabilityLoaded(false);
  };

  const handleDateChange = (newDate: Dayjs | null) => {
    setSelectedDate(newDate);
    setBulkOrderItems([]);
    setSelectedStores(new Set());
  };

  // Quantity handlers 
  const handleQuantityChange = async (
    storeNumber: string,
    itemId: number,
    itemSku: string,
    newQuantity: number,
  ): Promise<void> => {
    if (!selectedDate) return;

    const originalItem = bulkOrderItems.find(
      (item) => item.storeNumber === storeNumber && item.itemId === itemId,
    );
    const originalQuantity = originalItem?.orderQuantity ?? 0;

    setBulkOrderItems((prev) => {
      const updated = prev.map((item) =>
        item.storeNumber === storeNumber && item.itemId === itemId
          ? { ...item, orderQuantity: newQuantity }
          : item,
      );
      bulkOrderItemsRef.current = updated;
      return updated;
    });

    try {
      const deliveryDate = selectedDate.format('DD-MM-YYYY');

      const singleItemUpdate: OrderFormItemUpdate = {
        id: itemId,
        sku: itemSku,
        dates: [{ date: deliveryDate, quantity: newQuantity }],
      };

      const updates: OrderFormUpdate = {
        storeNumber: storeNumber,
        orderTypeId: orderType,
        supplierNumber: isDsdOrderType ? supplierNumber : '',
        items: [singleItemUpdate],
        dates: [deliveryDate],
      };

      const response = await client.saveOrderForm(storeNumber, updates);

      if (!response.success) {
        handleSnackbarOpen(response.message || t('autosaveFailed'), 'error');
        setShowQuantityWarningDialog(true);
        setWarningMessage(response.message);

        if (originalItem) {
          let correctedQuantity = newQuantity;
          if (originalItem.minQuantity != null && newQuantity < originalItem.minQuantity) {
            correctedQuantity = originalItem.minQuantity;
          } else if (originalItem.maxQuantity != null && newQuantity > originalItem.maxQuantity) {
            correctedQuantity = originalItem.maxQuantity;
          }

          setBulkOrderItems((prev) =>
            prev.map((item) =>
              item.storeNumber === storeNumber && item.itemId === itemId
                ? { ...item, orderQuantity: correctedQuantity }
                : item,
            ),
          );
        }
      }
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('autosaveFailed') + ' ' + error, 'error');

      setBulkOrderItems((prev) =>
        prev.map((item) => {
          if (item.storeNumber === storeNumber && item.itemId === itemId) {
            return { ...item, orderQuantity: originalQuantity };
          }
          return item;
        }),
      );
    }
  };

  const handleOverrideChange = useCallback(async (
    storeNumber: string,
    itemId: number,
    itemSku: string,
    overrideQty: number | null,
  ): Promise<void> => {
    if (!selectedDate) return;

    const originalItem = bulkOrderItemsRef.current.find(
      (item) => item.storeNumber === storeNumber && item.itemId === itemId,
    );
    const originalBaseQty = originalItem?.baseOrderQuantity ?? null;
    const originalOrderQty = originalItem?.orderQuantity ?? 0;

    const computeOptimisticOrderQty = (
      base: number | null,
      parQuantity: number | undefined,
      modifiers: BulkOrderItem['demandModifiers'],
    ): number => {
      const effectiveBase = base ?? parQuantity ?? 0;

      if (effectiveBase <= 0) return 0;
      const mods = modifiers ?? [];
      if (mods.length === 0) return Math.max(0, Math.round(effectiveBase));
      const totalPct = mods
        .filter(m => m.modifierType === DemandModifierType.PercentageUplift)
        .reduce((sum, m) => sum + m.modifierValue, 0);
      const totalOffset = mods
        .filter(m => m.modifierType === DemandModifierType.QuantityOffset)
        .reduce((sum, m) => sum + m.modifierValue, 0);
      return Math.max(0, Math.round((effectiveBase * (1 + totalPct / 100)) + totalOffset));
    };

    
    setBulkOrderItems((prev) => {
      const updated = prev.map((item) => {
        if (item.storeNumber === storeNumber && item.itemId === itemId) {
          const newBase = overrideQty;
          const newOrderQty = computeOptimisticOrderQty(newBase, item.par, item.demandModifiers);
          return { ...item, baseOrderQuantity: newBase, orderQuantity: newOrderQty };
        }
        return item;
      });
      bulkOrderItemsRef.current = updated;
      return updated;
    });

    const rollback = () => {
      setBulkOrderItems((prev) =>
        prev.map((item) =>
          item.storeNumber === storeNumber && item.itemId === itemId
            ? { ...item, baseOrderQuantity: originalBaseQty, orderQuantity: originalOrderQty }
            : item,
        ),
      );
    };

    const finalQty = overrideQty;
    const deliveryDate = selectedDate.format('DD-MM-YYYY');

    let errorMessage: string | undefined;
    try {
      const singleItemUpdate: OrderFormItemUpdate = {
        id: itemId,
        sku: itemSku,
        dates: [{ date: deliveryDate, quantity: finalQty }],
      };

      const updates: OrderFormUpdate = {
        storeNumber: storeNumber,
        orderTypeId: orderType,
        supplierNumber: isDsdOrderType ? supplierNumber : '',
        items: [singleItemUpdate],
        dates: [deliveryDate],
      };

      const response = await client.saveOrderForm(storeNumber, updates);

      if (!response.success) {
        errorMessage = response.message || t('autosaveFailed');
        setShowQuantityWarningDialog(true);
        setWarningMessage(response.message);
      }
    } catch (error) {
      logError(error);
      errorMessage = t('autosaveFailed') + ' ' + error;
    }

    if (errorMessage !== undefined) {
      rollback();
      handleSnackbarOpen(errorMessage, 'error');
      throw new Error(errorMessage);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, orderType, supplierNumber, isDsdOrderType, t]);

  // Download / Upload / Submit
  const handleDownload = async () => {
    if (!selectedDate) {
      handleSnackbarOpen(t('pleaseSelectDate'), 'warning');
      return;
    }

    setLoading(true);
    try {
      const request: BulkOrdersRequest = {
        customerTypeId: Number(selectedMarket),
        orderTypeId: orderType,
        deliveryDate: selectedDate.format('YYYY-MM-DD'),
        supplierNumber: isDsdOrderType ? supplierNumber : undefined,
        categoryId: appliedCategory !== -1 ? appliedCategory : undefined,
        itemSearch: appliedItemSearch || undefined,
      };

      const result = await client.downloadBulkOrders(request);
      if (result && result.success) {
        const blob = base64ToBlob(
          result.data.fileData,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        );
        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(blob);
        downloadLink.download = result.data.fileName;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        handleSnackbarOpen(t('downloadSuccess'), 'success');
      }
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('downloadFailed') + ' ' + error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!selectedDate) {
      handleSnackbarOpen(t('pleaseSelectDate'), 'warning');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64 = e.target?.result?.toString().split(',')[1] ?? '';
          const fileRequest = { fileData: base64, fileName: file.name };
          setLoading(true);

          const response = await client.uploadBulkOrders(
            Number(selectedMarket),
            orderType,
            selectedDate.format('YYYY-MM-DD'),
            fileRequest,
            isDsdOrderType ? supplierNumber : undefined,
          );
          if (response?.success) {
            handleSnackbarOpen(t('uploadSuccess'), 'success');
            await fetchBulkOrderData();
          } else {
            handleSnackbarOpen(response?.message || t('uploadFailed'), 'error');
          }
        } catch (error) {
          logError(error);
          handleSnackbarOpen(t('uploadFailed') + ' ' + error, 'error');
        } finally {
          setLoading(false);
        }
      };
      reader.onerror = () => {
        handleSnackbarOpen(t('fileReadError'), 'error');
        setLoading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('uploadFailed') + ' ' + error, 'error');
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedStores.size === 0) {
      handleSnackbarOpen(t('noRowsSelected'), 'warning');
      return;
    }

    try {
      setLoading(true);

      const selectedItems = bulkOrderItemsRef.current.filter((item) => selectedStores.has(item.storeNumber));

      // If any selected order is a standard scheduled order, SubmitScheduledOrders permission is required
      const hasScheduledOrders = selectedItems.some(
        item => item.orderId && item.orderId > 0 && !item.isEmergencyOrder,
      );
      if (hasScheduledOrders && !hasPermissionTo(['SubmitScheduledOrders'])) {
        handleSnackbarOpen(t('noScheduledOrderPermission'), 'error');
        return;
      }

      // MOV validation
      type MovInvalidOrder = { key: string; label: string };
      const movInvalidOrders: MovInvalidOrder[] = [];
      let movValue = 0;

      if (hasScheduledOrders) {
        // Supplier-specific MOV applies to DSD order types; otherwise (and as a fallback) the
        // order-type level MOV applies, so any order type can enforce a minimum order value.
        const supplierMov = (isDsdOrderType && supplierNumber)
          ? (suppliers.find(s => s.supplierNumber === supplierNumber)?.minimumOrderValue ?? 0)
          : 0;
        const orderTypeMov = orderTypes.find(ot => ot.id === orderType)?.minimumOrderValue ?? 0;
        movValue = supplierMov > 0 ? supplierMov : orderTypeMov;

        if (movValue > 0) {
      
          const scheduledOrderKeys = new Set(
            selectedItems
              .filter(item => item.orderId && item.orderId > 0 && !item.isEmergencyOrder)
              .map(item => `${item.storeNumber}-${item.orderId}`),
          );

    
          const orderTotals = new Map<string, { storeName: string; orderId: number; total: number }>();
          bulkOrderItemsRef.current
            .filter(item => item.orderId && scheduledOrderKeys.has(`${item.storeNumber}-${item.orderId}`))
            .forEach(item => {
              const key = `${item.storeNumber}-${item.orderId}`;
              if (!orderTotals.has(key)) {
                orderTotals.set(key, { storeName: item.storeName, orderId: item.orderId!, total: 0 });
              }
              orderTotals.get(key)!.total += (item.orderQuantity ?? 0) * (item.price ?? 0);
            });

          orderTotals.forEach((order, key) => {
            if (order.total < movValue) {
              movInvalidOrders.push({ key, label: `${order.storeName} (order #${order.orderId})` });
            }
          });
        }
      }

      const movInvalidKeys = new Set(movInvalidOrders.map(o => o.key));

      // Build submission payload, excluding orders that fail MOV
      const ordersByStoreAndId = selectedItems.reduce((acc, item) => {
        if (!item.orderId) return acc;
        const key = `${item.storeNumber}-${item.orderId}`;
        if (movInvalidKeys.has(key)) return acc;
        if (!acc[key]) {
          acc[key] = { storeNumber: item.storeNumber, orderId: item.orderId };
        }
        return acc;
      }, {} as Record<string, OrderSubmitItem>);

      const orders = Object.values(ordersByStoreAndId);

      if (orders.length === 0 && movInvalidOrders.length > 0) {
        handleSnackbarOpen(
          t('movNotMet', { stores: movInvalidOrders.slice(0, 5).map(o => o.label).join(', '), value: movValue }),
          'error',
        );
        return;
      }

      if (orders.length === 0) {
        handleSnackbarOpen(t('noOrdersToSubmit'), 'warning');
        return;
      }

      const movSkippedSuffix = movInvalidOrders.length > 0
        ? ` ${t('movSkipped', { count: movInvalidOrders.length, stores: movInvalidOrders.slice(0, 3).map(o => o.label).join(', '), value: movValue })}`
        : '';

      handleSnackbarOpen(t('submitStarted'), 'info');

      const batchRequest: BulkOrderSubmitRequest = { orders };
      const result = await client.submitBulkOrders(batchRequest);

      if (!result.success) {
        handleSnackbarOpen(result.message || t('submitFailed'), 'error');
        return;
      }

      const response = result.data;

      if (response.successCount > 0 && response.failureCount === 0) {
        const alreadySubmittedCount = response.results.filter(r => r.alreadySubmitted).length;
        const newlySubmittedCount = response.successCount - alreadySubmittedCount;

        let message = '';
        if (newlySubmittedCount > 0 && alreadySubmittedCount > 0) {
          message = t('submitMixedResult', { newlySubmittedCount, alreadySubmittedCount });
        } else if (alreadySubmittedCount > 0) {
          message = t('submitAlreadySubmittedOnly', { alreadySubmittedCount });
        } else {
          message = t('submitSuccessWithCount', { successCount: response.successCount });
        }

        handleSnackbarOpen(message + movSkippedSuffix, movInvalidOrders.length > 0 ? 'warning' : 'success');
        await fetchBulkOrderData();
        setSelectedStores(new Set());
      } else if (response.successCount > 0 && response.failureCount > 0) {
        const failedStores = response.results
          .filter(r => !r.success)
          .map(r => `${r.storeNumber} (${r.message})`)
          .slice(0, 3);

        const moreFailures = response.failureCount > 3
          ? t('submitMoreFailures', { count: response.failureCount - 3 })
          : '';
        handleSnackbarOpen(
          t('submitPartiallyCompleted', {
            successCount: response.successCount,
            failureCount: response.failureCount,
            failedStores: failedStores.join('; '),
            moreFailures,
          }) + movSkippedSuffix,
          'warning',
        );
        await fetchBulkOrderData();
      } else {
        const errorMessages = response.results
          .filter(r => !r.success)
          .map(r => `${r.storeNumber}: ${r.message}`)
          .slice(0, 3)
          .join('; ');

        const moreFailures = response.failureCount > 3
          ? t('submitMoreFailures', { count: response.failureCount - 3 })
          : '';
        handleSnackbarOpen(`${t('submitFailed')} ${errorMessages}${moreFailures}`, 'error');
      }
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('submitFailed') + ' ' + error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDemandModifierModal = (modifier?: DemandModifier) => {
    setSkuError('');
    setEditingModifier(modifier ?? null);
    setIsDemandModifierModalOpen(true);
  };

  const handleCloseDemandModifierModal = () => {
    setSkuError('');
    setIsDemandModifierModalOpen(false);
    setEditingModifier(null);
  };

  const handleSaveDemandModifier = async (modifier: DemandModifier) => {
    try {
      const result = await saveDemandModifier(modifier);
      if (!result.success) {
        if (
          modifier.targetType === 'specificSKUs' &&
          result.message?.includes('do not exist')
        ) {
          setSkuError(result.message);
        } else {
          handleSnackbarOpen(result.message || t('fetchOrderError'), 'error');
        }
        return;
      }

      if (result.message) {
        handleSnackbarOpen(result.message, 'warning');
      } else {
        handleSnackbarOpen(modifier.id ? t('demandModifierUpdated') : t('demandModifierCreated'), 'success');
      }
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('fetchOrderError') + ' ' + error, 'error');
      return;
    }

    handleCloseDemandModifierModal();
    await fetchBulkOrderData();
  };

  const handlePromoClick = async (storeNumber: string, itemId: number, modifierId?: number) => {
    if (!modifierId) {
      handleOpenDemandModifierModal();
      return;
    }

    try {
      setLoading(true);
      const result = await client.getDemandModifierById(storeNumber, modifierId);
      if (result.success && result.data) {
        handleOpenDemandModifierModal(result.data);
      } else {
        handleSnackbarOpen(result.message || t('fetchOrderError'), 'error');
      }
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('fetchOrderError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = (): boolean => {
    if (loading || bulkOrderItems.length === 0 || selectedStores.size === 0) return false;

    const selectedItems = bulkOrderItems.filter((item) => selectedStores.has(item.storeNumber));

    const hasValidOrders = selectedItems.some(item => item.orderId && item.orderId > 0);
    const hasItemsWithQuantity = selectedItems.some(item => item.orderQuantity > 0);

    // If any selected order is scheduled, require SubmitScheduledOrders permission
    const hasScheduledOrders = selectedItems.some(
      item => item.orderId && item.orderId > 0 && !item.isEmergencyOrder,
    );
    if (hasScheduledOrders && !hasPermissionTo(['SubmitScheduledOrders'])) return false;

    return hasValidOrders && hasItemsWithQuantity;
  };

  const hasAvailableOdsDates = Object.values(odsAvailability).some(
    (availability) => availability.availableStores > 0,
  );

  const showNoDeliveryDatesMessage =
    !!selectedMarket && orderType !== -1 && odsAvailabilityLoaded && !hasAvailableOdsDates;

  return {
    orderState: {
      loading,
      selectedMarket,
      orderType,
      supplierNumber,
      selectedDate,
      orderTypes,
      suppliers,
      categories,
      markets,
      bulkOrderItems,
      selectedStores,
      odsAvailability,
      odsAvailabilityLoaded,
      isDsdOrderType,
      bulkOrdersLoadVersion,
      categoryOptions,
      getCategoryOptionLabel,
      canSubmit,
      hasAvailableOdsDates,
      showNoDeliveryDatesMessage,
    },
    filterHandlers: {
      handleMarketChange,
      handleOrderTypeChange,
      handleSupplierChange,
      handleDateChange,
    },
    selectionHandlers: {
      handleStoreGroupSelection,
    },
    dataHandlers: {
      handleQuantityChange,
      handleOverrideChange,
      handleDownload,
      handleFileUpload,
      handleSubmit,
      fetchBulkOrderData,
    },
    quantityWarning: {
      showQuantityWarningDialog,
      setShowQuantityWarningDialog,
      warningMessage,
    },
    notification: {
      snackbar,
      handleSnackbarOpen,
      handleSnackbarClose,
    },
    demandModifier: {
      isDemandModifierModalOpen,
      editingModifier,
      skuError,
      setSkuError,
      handleOpenDemandModifierModal,
      handleCloseDemandModifierModal,
      handleSaveDemandModifier,
      handlePromoClick,
    },
  };
};
